import {
  AnalysisNewsEntry,
  AnalyzedVersionPerMarket,
  CloneAnalysisRequest,
  CloneAnalysisToMarketRequest,
  CreateAnalysisForVersionRequest,
  CreateAnalysisRequest,
  DeleteAnalysisRequest,
  GameAnalysis,
  GameVersion,
  SaveVersionTagsRequest,
} from '../features/games/types/GameAnalyses'
import { customError, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'
import { internalFeedApi } from './internal-feed'

const marketSettingUrl = 'users/settings/internal_analysis_list_selected_market_iso'
const analysesRequest = (gameId: string, marketIso: string) => ({ url: `games/${gameId}/analyses`, params: { marketIso } })
const versionsRequest = (appId: number, marketIso: string) => ({ url: `versions/${appId}/list`, params: { marketIso } })

const readMarketSetting = (setting: unknown) => (setting as { marketIso?: string } | null)?.marketIso ?? null
const toArray = <T>(data: unknown) => (Array.isArray(data) ? (data as T[]) : [])

// Legacy opens the created analysis from the answer's `id`; an answer without one isn't a created analysis.
const createdAnalysisId = (data: unknown) => {
  const id = (data as { id?: unknown } | null)?.id
  return typeof id === 'string' && id ? { data: id } : { error: customError('The created analysis has no id') }
}

const gameAnalysesApi = internalApiserverApi
  .enhanceEndpoints({ addTagTypes: ['gameAnalyses', 'gameVersions', 'analysisListMarketSetting', 'internalGameDocument'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getGameAnalyses: builder.query<GameAnalysis[], { gameId: string; marketIso: string }>({
        query: ({ gameId, marketIso }) => analysesRequest(gameId, marketIso),
        transformResponse: (data: unknown) => toArray<GameAnalysis>(data),
        providesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      // Newest first. Without `marketIso` the backend answers `us`.
      getGameVersions: builder.query<GameVersion[], { appId: number; marketIso: string }>({
        query: ({ appId, marketIso }) => versionsRequest(appId, marketIso),
        transformResponse: (data: unknown) => toArray<GameVersion>(data),
        providesTags: (_result, _error, { appId }) => [{ type: 'gameVersions', id: appId }],
      }),
      getAnalyzedVersionsPerMarket: builder.query<AnalyzedVersionPerMarket[], string>({
        query: (gameId) => `i/analyses/versions/${gameId}/latest`,
        transformResponse: (data: unknown) => toArray<AnalyzedVersionPerMarket>(data),
        providesTags: (_result, _error, gameId) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      // An unset setting answers 200 with an empty body.
      getAnalysisListMarketSetting: builder.query<string | null, void>({
        query: () => ({ url: marketSettingUrl, responseHandler: optionalJson }),
        transformResponse: readMarketSetting,
        providesTags: ['analysisListMarketSetting'],
      }),
      // Returns the market as stored afterwards.
      saveAnalysisListMarketSetting: builder.mutation<string | null, string>({
        async queryFn(marketIso, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: marketSettingUrl, method: 'PUT', body: { marketIso }, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const stored = await baseQuery({ url: marketSettingUrl, responseHandler: optionalJson })
          if (stored.error) return { error: stored.error }
          return { data: readMarketSetting(stored.data) }
        },
        async onQueryStarted(_marketIso, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            dispatch(gameAnalysesApi.util.upsertQueryData('getAnalysisListMarketSetting', undefined, data))
          } catch {}
        },
      }),
      // Each create returns the new analysis' id.
      createAnalysis: builder.mutation<string, CreateAnalysisRequest>({
        async queryFn(body, _api, _extraOptions, baseQuery) {
          const created = await baseQuery({ url: 'i/analyses', method: 'POST', body })
          return created.error ? { error: created.error } : createdAnalysisId(created.data)
        },
        invalidatesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      cloneAnalysis: builder.mutation<string, CloneAnalysisRequest>({
        async queryFn({ id }, _api, _extraOptions, baseQuery) {
          const created = await baseQuery({ url: `i/analyses/${id}`, method: 'POST', body: { id } })
          return created.error ? { error: created.error } : createdAnalysisId(created.data)
        },
        invalidatesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      // `gameVersion` is a version or `latest`.
      cloneAnalysisToMarket: builder.mutation<string, CloneAnalysisToMarketRequest>({
        async queryFn(body, _api, _extraOptions, baseQuery) {
          const { gameId, sourceMarketIso, targetMarketIso, gameVersion } = body
          const url = `i/analyses/${encodeURIComponent(gameVersion)}/${gameId}/from/${encodeURIComponent(sourceMarketIso)}/to/${encodeURIComponent(
            targetMarketIso
          )}`
          const created = await baseQuery({ url, method: 'POST', body })
          return created.error ? { error: created.error } : createdAnalysisId(created.data)
        },
        invalidatesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      createAnalysisForVersion: builder.mutation<string, CreateAnalysisForVersionRequest>({
        async queryFn({ version, marketId, gameId }, _api, _extraOptions, baseQuery) {
          const created = await baseQuery({ url: `i/analyses/version/${encodeURIComponent(version)}`, method: 'POST', body: { marketId, gameId } })
          return created.error ? { error: created.error } : createdAnalysisId(created.data)
        },
        invalidatesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }],
      }),
      // Returns the market's analyses as listed afterwards, so the caller can check the analysis is gone.
      deleteAnalysis: builder.mutation<GameAnalysis[], DeleteAnalysisRequest>({
        async queryFn({ id, gameId, marketIso }, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `i/analyses/${id}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const remaining = await baseQuery(analysesRequest(gameId, marketIso))
          if (remaining.error) return { error: remaining.error }
          return { data: toArray<GameAnalysis>(remaining.data) }
        },
        invalidatesTags: (_result, _error, { gameId }) => [{ type: 'gameAnalyses', id: gameId }, 'gameVersions'],
      }),
      // The answer's shape is unknown, so the game, its versions and analyses are read again instead.
      checkGameVersion: builder.mutation<null, string>({
        async queryFn(gameId, _api, _extraOptions, baseQuery) {
          const checked = await baseQuery({ url: `i/games/versioncheck/${gameId}`, method: 'PUT', body: { gameId }, responseHandler: 'text' })
          return checked.error ? { error: checked.error } : { data: null }
        },
        invalidatesTags: (_result, _error, gameId) => [{ type: 'internalGameDocument', id: gameId }, { type: 'gameAnalyses', id: gameId }, 'gameVersions'],
      }),
      // Tags are per app and version, the same in every market. Returns the version as listed afterwards in `marketIso`.
      saveVersionTags: builder.mutation<GameVersion | undefined, SaveVersionTagsRequest>({
        async queryFn({ appId, version, tags, marketIso }, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/versiontags/tag', method: 'PUT', body: { appId, version, tags }, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const stored = await baseQuery(versionsRequest(appId, marketIso))
          if (stored.error) return { error: stored.error }
          return { data: toArray<GameVersion>(stored.data).find((candidate) => candidate.version === version) }
        },
        invalidatesTags: (_result, _error, { appId }) => [{ type: 'gameVersions', id: appId }],
      }),
    }),
  })

const analysisNewsApi = internalFeedApi.injectEndpoints({
  endpoints: (builder) => ({
    // One id only: two or more answer 500. An analysis without a news entry answers an empty body.
    getAnalysisNewsEntry: builder.query<AnalysisNewsEntry | null, string>({
      query: (analysisId) => ({ url: `entries/analysis/${analysisId}`, responseHandler: optionalJson }),
      transformResponse: (entry: AnalysisNewsEntry | AnalysisNewsEntry[] | null) => (Array.isArray(entry) ? entry[0] ?? null : entry),
    }),
  }),
})

export const {
  useGetGameAnalysesQuery,
  useGetGameVersionsQuery,
  useGetAnalyzedVersionsPerMarketQuery,
  useGetAnalysisListMarketSettingQuery,
  useSaveAnalysisListMarketSettingMutation,
  useCreateAnalysisMutation,
  useCloneAnalysisMutation,
  useCloneAnalysisToMarketMutation,
  useCreateAnalysisForVersionMutation,
  useDeleteAnalysisMutation,
  useCheckGameVersionMutation,
  useSaveVersionTagsMutation,
} = gameAnalysesApi

export const { useGetAnalysisNewsEntryQuery } = analysisNewsApi

type GameAnalysesApiState = Parameters<typeof gameAnalysesApi.util.selectCachedArgsForQuery>[0]

// The app's versions per market already in the cache; it never starts a read.
export const selectCachedGameVersionsByMarket = (state: GameAnalysesApiState, appId?: number | null): Record<string, GameVersion[]> =>
  gameAnalysesApi.util
    .selectCachedArgsForQuery(state, 'getGameVersions')
    .filter((args) => !!appId && args.appId === appId)
    .reduce<Record<string, GameVersion[]>>((byMarket, args) => {
      const data = gameAnalysesApi.endpoints.getGameVersions.select(args)(state).data
      return data ? { ...byMarket, [args.marketIso]: data } : byMarket
    }, {})
